import { useMemo } from 'react';
import pb from '@libs/pocketbase';
import { useAuthStore, type User } from '@state/auth.store';
import type { Permission } from '@config/permissions';
import { PERMISSIONS } from '@config/permissions';
import { ROLES } from '@config/constants';

export function useAuth() {
  const { user, setUser, clearUser } = useAuthStore();

  const isAuthenticated = useMemo(() => {
    return pb.authStore.isValid && !!user;
  }, [user]);

  const isLoading = false;

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    if (user.permissions?.includes(PERMISSIONS.ALL)) return true;
    return user.permissions?.includes(permission) ?? false;
  };

  const loginUser = async (identity: string, password: string) => {
    const authData = await pb.collection('users').authWithPassword(identity, password);
    const appUser: User = {
      ...(authData.record as User),
      isSuperAdmin: false,
      collection: 'users',
    };
    setUser(appUser);
    return { ...authData, record: appUser };
  };

  const loginSuperAdmin = async (identity: string, password: string) => {
    const authData = await pb.collection('super_admins').authWithPassword(identity, password);
    const superAdminUser: User = {
      ...(authData.record as User),
      role: ROLES.SUPER_ADMIN,
      isSuperAdmin: true,
      collection: 'super_admins',
    };
    setUser(superAdminUser);
    return { ...authData, record: superAdminUser };
  };

  const logout = () => {
    pb.authStore.clear();
    clearUser();
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    hasPermission,
    loginUser,
    loginSuperAdmin,
    logout,
  };
}

